import * as z from "zod/v4";
import type { BookingTag } from "../../../common/jsonRpcTypes";

export interface BookingPrefill {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dob?: { day: string; month: string; year: string };
  notes?: string;
  customFieldValue?: string;
  tags?: Record<string, TagValue>;
}

export type TagValue = string | string[] | boolean;

const nameSchema = z.string().trim().min(1).max(100);
const emailSchema = z.string().trim().max(254).pipe(z.email());
const phoneSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^\+?[\d\s()-]{5,30}$/);
const notesSchema = z.string().trim().min(1).max(1000);
const customFieldValueSchema = z.string().trim().min(1).max(500);
const dobSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isValidDob);
const tagsSchema = z
  .record(
    z.string().trim().max(64),
    z.union([z.string().trim().max(500), z.array(z.string().trim().max(500)).max(50), z.boolean()]),
  )
  .refine((tags) => Object.keys(tags).length <= 20);

function isValidDob(value: string): boolean {
  const [yearValue, monthValue, dayValue] = value.split("-");
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(Date.UTC(year, month - 1, day));
  const today = new Date();
  const currentDate = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

  return (
    year >= 1900 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getTime() <= currentDate
  );
}

function parseStringParam(searchParams: URLSearchParams, name: string, schema: z.ZodType<string>): string | undefined {
  const rawValue = searchParams.get(name);
  if (rawValue === null) return undefined;

  const result = schema.safeParse(rawValue);
  return result.success ? result.data : undefined;
}

function parseDobParam(searchParams: URLSearchParams): BookingPrefill["dob"] | undefined {
  const value = parseStringParam(searchParams, "dob", dobSchema);
  if (!value) return undefined;
  return parseIsoDateToDobFields(value) ?? undefined;
}

function parseTagsParam(searchParams: URLSearchParams): BookingPrefill["tags"] | undefined {
  const rawValue = searchParams.get("tags");
  if (rawValue === null || rawValue.trim().length === 0) return undefined;

  try {
    const result = tagsSchema.safeParse(JSON.parse(rawValue));
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

export function parseBookingPrefill(searchParams: URLSearchParams): BookingPrefill {
  const firstName = parseStringParam(searchParams, "first_name", nameSchema);
  const lastName = parseStringParam(searchParams, "last_name", nameSchema);
  const email = parseStringParam(searchParams, "email", emailSchema);
  const phone = parseStringParam(searchParams, "phone", phoneSchema);
  const dob = parseDobParam(searchParams);
  const notes = parseStringParam(searchParams, "notes", notesSchema);
  const customFieldValue = parseStringParam(searchParams, "custom_field_value", customFieldValueSchema);
  const tags = parseTagsParam(searchParams);

  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(dob ? { dob } : {}),
    ...(notes ? { notes } : {}),
    ...(customFieldValue ? { customFieldValue } : {}),
    ...(tags ? { tags } : {}),
  };
}

function coerceSelectTagValue(value: TagValue, tag: BookingTag): TagValue | undefined {
  if (typeof value !== "string") return undefined;
  if (tag.options !== undefined && !tag.options.includes(value)) return undefined;
  return value;
}

function coerceMultiselectTagValue(value: TagValue, tag: BookingTag): TagValue | undefined {
  if (!Array.isArray(value)) return undefined;
  const options = tag.options;
  if (options === undefined) return value;
  return value.filter((option) => options.includes(option));
}

function coerceBooleanTagValue(value: TagValue): TagValue | undefined {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  return undefined;
}

function coerceTagValue(value: TagValue, tag: BookingTag): TagValue | undefined {
  switch (tag.type) {
    case "select":
      return coerceSelectTagValue(value, tag);
    case "multiselect":
      return coerceMultiselectTagValue(value, tag);
    case "text":
      return typeof value === "string" ? value : undefined;
    case "boolean":
      return coerceBooleanTagValue(value);
    default:
      return undefined;
  }
}

export function applyTagPrefill(
  prefillTags: BookingPrefill["tags"],
  tags: readonly BookingTag[],
): Record<string, TagValue> {
  const result: Record<string, TagValue> = {};
  if (!prefillTags) return result;

  for (const [tagId, value] of Object.entries(prefillTags)) {
    const tag = tags.find((candidate) => candidate.id === tagId);
    if (!tag) continue;

    const coercedValue = coerceTagValue(value, tag);
    if (coercedValue !== undefined) result[tagId] = coercedValue;
  }

  return result;
}

export function getSafeReturnUrl(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;

  try {
    const url = new URL(raw);
    return url.protocol === "http:" || url.protocol === "https:" ? raw : undefined;
  } catch {
    return undefined;
  }
}

/** Parse an ISO date string to day, month, year fields for DOB inputs. */
export function parseIsoDateToDobFields(
  isoDateString: string | null | undefined,
): { day: string; month: string; year: string } | null {
  if (!isoDateString) {
    return null;
  }

  try {
    const datePart = isoDateString.split("T")[0];
    const parts = datePart.split("-");

    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];

      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);

      if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
        return {
          day: day,
          month: month,
          year: year,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}
